import { CalendarPlus as CalendarPlusIcon } from 'iconoir-react'
import { entries, startCase } from 'lodash'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import Spinner from '../Spinner'
import { ActivityCard } from './ActivityCard'
import { ActivityTags } from './ActivityTags'
import { KeyValueDrawer } from './KeyValueDrawer'
import { formatDate } from 'app/date.utils'
import { formatPhoneNumber } from 'app/formatPhoneNumber'
import { useShowUtmFields } from 'app/hooks/useShowUtmFields'
import { gqlFetchOne } from 'app/lib/graphqlClient'
import type { ActivityJson } from 'app/types/entities'
import type { SchedulerJson } from 'app/types/entities'
import { useWorkspace } from 'app/utils'
import { assertBoom } from 'lib/assertBoom'
import { logError } from 'lib/logging'

type Props = {
  activity: ActivityJson
  alwaysOpen?: boolean
  isFirst?: boolean
  isLast?: boolean
  singleActivity?: boolean
}
/**
 * SchedulerCard renders the activity created from the trackable Online Scheduler
 * or the In App Scheduler (distinguished by the scheduler record's `source` field).
 */
export function SchedulerCard({ activity, alwaysOpen, isFirst, isLast, singleActivity }: Props): ReactElement {
  assertBoom(`Scheduler` === activity.type!, `Invalid activity type`)
  const workspace = useWorkspace()
  const showUtmFields = useShowUtmFields()
  const [scheduler, setScheduler] = useState<SchedulerJson>()
  const [isLoading, setIsLoading] = useState(true)
  const [bookedByName, setBookedByName] = useState<string>()

  useEffect(() => {
    let isMounted = true
    setIsLoading(true)
    gqlFetchOne<SchedulerJson>(
      `scheduler`,
      activity.typeId!,
      `id name phone email birthDate day time extraFields source bookedByUserId`,
    )
      .then((record) => {
        if (!isMounted) return
        setScheduler(record)
        if (record?.bookedByUserId) {
          gqlFetchOne<{ id: string; name?: string }>(`user`, record.bookedByUserId, `id name`)
            .then((user) => {
              if (isMounted) setBookedByName(user?.name || undefined)
            })
            .catch(() => {})
        }
      })
      .catch((err) => {
        logError(`Error fetching scheduler activity record`, err)
      })
      .finally(() => {
        if (isMounted) setIsLoading(false)
      })
    return () => {
      isMounted = false
    }
  }, [activity.typeId])

  const name = useMemo(() => scheduler?.name || `-`, [scheduler])
  const isInApp = scheduler?.source === `app`
  const title = isInApp ? `In App Scheduler` : `Online Scheduler`

  const extraFields = scheduler?.extraFields
    ? entries(JSON.parse(scheduler?.extraFields)).reduce(
        (acc, [key, value]) => ({ ...acc, [startCase(key)]: value }),
        {},
      )
    : undefined

  return (
    <ActivityCard
      title={<span className="text-ui-sm text-strong">{title}</span>}
      activityDate={activity?.activityDate}
      icon={<CalendarPlusIcon className="h-5 w-5 rounded-md bg-green-600 p-1 text-on-color" />}
      tags={<ActivityTags activityTags={activity?.tags} workspaceId={workspace?.id} />}
      preview={
        <div className="w-full items-center rounded-lg border border-default bg-surface px-4 py-3 text-body-xs shadow-sm">
          <KeyValueDrawer
            cols={2}
            map={{
              Name: startCase(name),
              'Phone Number': formatPhoneNumber(scheduler?.phone) || `-`,
              Email: activity?.email || `-`,
              'Birth Date': formatDate({ value: scheduler?.birthDate, dateOnly: true }) || `-`,
            }}
            className={{ 'Birth Date': `phi` }}
          />
        </div>
      }
      drawer={
        <div
          className={`w-full items-center rounded-lg border border-default bg-surface px-4 py-3 text-body-xs shadow-sm ${
            isLoading ? `justify-center` : ``
          }`}
        >
          {activity && (
            <>
              {isLoading ? (
                <Spinner size={4} />
              ) : (
                <KeyValueDrawer
                  cols={2}
                  map={{
                    Name: startCase(name),
                    'Phone Number': formatPhoneNumber(scheduler?.phone) || `-`,
                    Email: scheduler?.email || `-`,
                    'Birth Date': formatDate({ value: scheduler?.birthDate, dateOnly: true }) || `-`,
                    'Appt. Time':
                      scheduler?.day && scheduler?.time
                        ? `${formatDate({ value: scheduler.day, dateOnly: true })} ${scheduler.time}`
                        : formatDate({ value: scheduler?.day }) || `-`,
                    ...(isInApp && { 'Booked By': bookedByName || `-` }),
                    ...(showUtmFields &&
                      !isInApp && {
                        Source: activity.utmSource,
                        Medium: activity.utmMedium,
                        Campaign: activity.utmCampaign,
                        Content: activity.utmContent,
                        Keywords: activity.utmKeywords,
                        GCLID: activity.gclid,
                        FBCLID: activity.fbclid,
                      }),
                    ...extraFields,
                  }}
                  className={{ 'Birth Date': `phi` }}
                />
              )}
            </>
          )}
        </div>
      }
      alwaysOpen={alwaysOpen}
      isFirst={isFirst}
      isLast={isLast}
      singleActivity={singleActivity}
    />
  )
}
